import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { confirmAlert } from 'react-confirm-alert'; 
import { useReactToPrint } from 'react-to-print';
import { useRef } from 'react'; 
import 'react-confirm-alert/src/react-confirm-alert.css'; 
import withRoleAccess from '../../hoc/withRoleAccess';

const RefundModal = ({ show, onHide, registration, onRefund }) => {
  const { t } = useTranslation();
  const [refundAmount, setRefundAmount] = useState(registration.final_price);
  const [refundReason, setRefundReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  const formatNumber = (number) => {
    if (isNaN(number)) return '0.00';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  const handleRefund = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    const refundData = {
      registrationId: registration.id,
      refundAmount,
      refundReason,
      userId: registration.student_id,
    };

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/registrations/refund`, refundData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.status === 200) {
        onRefund(response.data);
        onHide();
        setTimeout(() => {
          confirmAlert({
            title: t('refundSuccessful'),
            message: t('refundProcessedMessage'),
            buttons: [
              {
                label: t('ok'),
                onClick: () => setIsSubmitting(false)
              }
            ]
          });
        }, 300);
      } else {
        alert(t('refundError'));
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error processing refund:', error);
      alert(t('refundError'));
      setIsSubmitting(false);
    }
  };

  const confirmRefund = () => {
    setTimeout(() => {
      confirmAlert({
        title: t('confirmRefund'),
        message: t('confirmRefundMessage'),
        buttons: [
          {
            label: t('yes'),
            onClick: handleRefund
          },
          {
            label: t('no'),
            onClick: () => setIsSubmitting(false)
          }
        ]
      });
    }, 300);
  };

  const componentRef = useRef();

  const handlePrint = useReactToPrint({
  content: () => componentRef.current,
});

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div ref={componentRef} className="refund-print-content" style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '500px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden'
        }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#fef2f2'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: '600',
            color: '#dc2626'
          }}>
            {t('processRefund')}
          </h2>
          <button
            onClick={onHide}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Registration Info */}
        <div style={{
          padding: '20px 24px',
          backgroundColor: '#f9fafb',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            fontSize: '13px',
            color: '#4b5563'
          }}>
            <div><strong>Invoice:</strong> {registration.invoiceno}</div>
            <div><strong>Student:</strong> {registration.student_name}</div>
            <div><strong>Class:</strong> {registration.class_code}</div>
            <div><strong>Total Paid:</strong> {formatNumber(registration.final_price)} THB</div>
          </div>
        </div>

        {/* Form Body */}
        <div style={{ padding: '24px' }}>
          {/* Refund Amount */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '6px',
              fontSize: '13px',
              fontWeight: '500',
              color: '#374151'
            }}>
              {t('refundAmount')}:
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                max={registration.final_price}
                min="0"
                step="0.01"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  paddingRight: '50px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
              <span style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '13px',
                color: '#6b7280',
                pointerEvents: 'none'
              }}>
                THB
              </span>
            </div>
            <div style={{
              fontSize: '12px',
              color: '#6b7280',
              marginTop: '4px'
            }}>
              Maximum refund: {formatNumber(registration.final_price)} THB
            </div>
          </div>

          {/* Refund Reason */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '6px',
              fontSize: '13px',
              fontWeight: '500',
              color: '#374151'
            }}>
              {t('refundReason')}:
            </label>
            <textarea
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder={t('refundReasonPlaceholder')}
              rows={4}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s',
                resize: 'vertical',
                minHeight: '80px',
                boxSizing: 'border-box',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>

          {/* Warning Notice */}
          <div style={{
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <span style={{
                color: '#f59e0b',
                fontSize: '16px',
                marginTop: '1px'
              }}>
                ⚠️
              </span>
              <div style={{
                fontSize: '12px',
                color: '#92400e',
                lineHeight: '1.4'
              }}>
                <strong>Warning:</strong> This action cannot be undone. The refund will be processed immediately and the registration status will be updated to "Refunded".
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={onHide}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: 'white',
              color: '#374151',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#f9fafb';
              e.target.style.borderColor = '#9ca3af';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.borderColor = '#d1d5db';
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => { onHide(); confirmRefund(); }}
            disabled={isSubmitting || !refundAmount || !refundReason.trim()}
            style={{
              padding: '8px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: isSubmitting || !refundAmount || !refundReason.trim() ? '#9ca3af' : '#dc2626',
              color: 'white',
              fontSize: '13px',
              fontWeight: '500',
              cursor: isSubmitting || !refundAmount || !refundReason.trim() ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
              opacity: isSubmitting ? 0.7 : 1
            }}
            onMouseOver={(e) => {
              if (!isSubmitting && refundAmount && refundReason.trim()) {
                e.target.style.backgroundColor = '#b91c1c';
              }
            }}
            onMouseOut={(e) => {
              if (!isSubmitting && refundAmount && refundReason.trim()) {
                e.target.style.backgroundColor = '#dc2626';
              }
            }}
          >
            {isSubmitting ? 'Processing...' : t('refund')}
          </button>
        </div>
      </div>
      <div className="no-print" style={{ textAlign: 'center', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
      <button
          onClick={handlePrint}
          style={{
            padding: '8px 16px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            backgroundColor: 'white',
            color: '#374151',
            fontSize: '13px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginRight: '8px'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#f9fafb';
            e.target.style.borderColor = '#9ca3af';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = 'white';
            e.target.style.borderColor = '#d1d5db';
          }}
        >
          Print
        </button></div>
    </div>
  );
};

export default withRoleAccess(RefundModal, ['admin', 'superadmin']);